import { WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * State 5 of `DESIGN.md` §8. A banner rather than a page: going offline must not throw away what
 * is on screen — the interview screen has to survive it (§6.6).
 *
 * ⚠️ **`state.offline` is the sentence for a failed *read*, and that is a split rather than a rename
 * (BUG-017).** It read *"You are offline. Changes will be sent when the connection returns."* on both
 * audiences at once, and **nothing in this client queues a mutation**: `createQueryClient` retries a
 * query twice on a dropped connection and a mutation not at all, so an offline write is lost. Here
 * there is no "change" to send in the first place — this state is rendered by `ApiErrorState` when a
 * **read** failed — so the sentence now says only what a read can promise, and the reader's own
 * **Retry** beside it is what performs the retry.
 *
 * A failed **write** gets `state.offlineWrite`, which denies the queue and names the check that settles
 * what the server did (§6.4's two mutations, §6.5's *Edit* and its status actions). Four screens keep
 * bespoke sentences instead, each naming the thing to reload — the erasure above all (ADR 0009,
 * `AnonymizeDialog`), and §6.8's and §6.9's writes. `src/i18n/i18n.test.tsx` walks the whole catalogue
 * for any string that promises a send, a save or a retry nothing performs.
 */
export function OfflineState() {
  const { t } = useTranslation('components');

  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded border border-rule bg-paper-sunken px-3 py-2 text-sm text-ink-700"
    >
      <WifiOff size={16} strokeWidth={1.5} className="text-ink-500" aria-hidden />
      {t('state.offline')}
    </div>
  );
}
